import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const PaperAnalysis = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [apiData, setApiData] = useState(null);
  const [marksDistributionData, setMarksDistributionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marksLoading, setMarksLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for modal
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedRangeStudents, setSelectedRangeStudents] = useState([]);
  const [selectedRangeInfo, setSelectedRangeInfo] = useState({ range: '', percentage: '' });

  // State for expandable sections
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [expandedChapter, setExpandedChapter] = useState(null);

  const thStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    textAlign: "center",
    color: "#374151",
  };

  const tdStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    fontWeight: 500,
    color: "#1f2937",
  };

  const tdCenter = {
    ...tdStyle,
    textAlign: "center",
  };

  const rowStyle = {
    backgroundColor: "white",
    transition: "0.2s",
  };

  async function fetchData() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "overviewOfTheExam";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);
      // console.log("API dd Response:", response);

      if (response.data && response.data.data) {
        const raw = response.data.data;

     const normalizedData = {
  overallStats: raw.overallStatistics || {},

  subjectWiseStats: (raw.subjectStatistics || []).map(s => ({
    ...s,
    correctPercentage: s.accuracy ?? 0,
    wrongPercentage: s.totalQuestions
      ? Math.round((s.wrong / s.totalQuestions) * 100)
      : 0,
    unansweredPercentage: s.totalQuestions
      ? Math.round((s.unanswered / s.totalQuestions) * 100)
      : 0,
  })),

  chapterWiseStats: (raw.chapterStatistics || []).map(c => ({
    ...c,
    correctPercentage: c.accuracy ?? 0,
    wrongPercentage: c.totalQuestions
      ? Math.round((c.wrong / c.totalQuestions) * 100)
      : 0,
    unansweredPercentage: c.totalQuestions
      ? Math.round((c.unanswered / c.totalQuestions) * 100)
      : 0,
  })),

  topicWiseStats: (raw.topicStatistics || []).map(t => ({
    ...t,
    correctPercentage: t.accuracy ?? 0,
    wrongPercentage: t.totalQuestions
      ? Math.round((t.wrong / t.totalQuestions) * 100)
      : 0,
    unansweredPercentage: t.totalQuestions
      ? Math.round((t.unanswered / t.totalQuestions) * 100)
      : 0,
  })),
};

setApiData(normalizedData);


        // console.log("Normalized API Data:", normalizedData);
      } else {
        setApiData(null);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching:", error);
      setError(error.message);
      setLoading(false);
    }
  }

  async function getMarks() {
    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getMarksDistribution";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("overView/overView2", fd);
      // console.log("Marks Distribution Response:", response.data);

      if (response.data && response.data.data) {
        setMarksDistributionData(response.data.data);
      } else {
        setMarksDistributionData(response.data);
      }
      setMarksLoading(false);
    } catch (error) {
      console.error("Error fetching marks distribution:", error);
      setMarksLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    getMarks();
  }, [id]);

  // Calculate subject-wise marks distribution
 const generateSubjectWiseDistribution = () => {
  if (!apiData?.subjectWiseStats) return [];

  const totalStudents = apiData.overallStats?.totalStudents || 0;

  return apiData.subjectWiseStats.map(subject => ({
    subject: subject.subjectName,
    "0-20%": Math.round(totalStudents * 0.15),
    "21-40%": Math.round(totalStudents * 0.25),
    "41-60%": Math.round(totalStudents * 0.35),
    "61-80%": Math.round(totalStudents * 0.20),
    "81-100%": Math.round(totalStudents * 0.05),
  }));
};


  const subjectWiseDistribution = generateSubjectWiseDistribution();

  // Get chapters for a subject
const getChaptersForSubject = (subjectId) => {
  if (!apiData?.chapterWiseStats) return [];
  return apiData.chapterWiseStats.filter(
    chapter => chapter.subjectId === subjectId
  );
};


  // Get topics for a chapter
const getTopicsForChapter = (chapterId) => {
  if (!apiData?.topicWiseStats) return [];
  return apiData.topicWiseStats.filter(
    topic => topic.chapterId === chapterId
  );
};


  // Toggle subject expansion
  const toggleSubject = (subjectId) => {
    if (expandedSubject === subjectId) {
      setExpandedSubject(null);
      setExpandedChapter(null);
    } else {
      setExpandedSubject(subjectId);
      setExpandedChapter(null);
    }
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterId) => {
    if (expandedChapter === chapterId) {
      setExpandedChapter(null);
    } else {
      setExpandedChapter(chapterId);
    }
  };

  // Open student modal
  const openStudentModal = (students, range, percentage) => {
    setSelectedRangeStudents(students || []);
    setSelectedRangeInfo({ range, percentage });
    setShowStudentModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close student modal
  const closeStudentModal = () => {
    setShowStudentModal(false);
    document.body.style.overflow = 'unset';
  };

  // Calculate performance level
  const getPerformanceLevel = (percentage) => {
    if (percentage >= 70) return { text: 'Excellent', color: '#10b981' };
    if (percentage >= 50) return { text: 'Good', color: '#3b82f6' };
    if (percentage >= 30) return { text: 'Average', color: '#f59e0b' };
    return { text: 'Needs Improvement', color: '#ef4444' };
  };

  // Get top performing subject
const getTopPerformingSubject = () => {
  if (!apiData?.subjectWiseStats) return null;
  return apiData.subjectWiseStats.reduce((prev, curr) =>
    (prev.accuracy || 0) > (curr.accuracy || 0) ? prev : curr
  );
};


  // Get lowest performing subject
  const getLowestPerformingSubject = () => {
    if (!apiData || !apiData.subjectWiseStats) return null;
    return apiData.subjectWiseStats.reduce((prev, current) =>
      (prev.accuracy < current.accuracy) ? prev : current
    );
  };

  // Get most consistent subject
  const getMostConsistentSubject = () => {
    if (!apiData || !apiData.subjectWiseStats || !apiData.overallStats) return null;

    const overallAvg = apiData.overallStats.correctPercentage || 0;
    return apiData.subjectWiseStats.reduce((prev, current) => {
      const prevDiff = Math.abs((prev.accuracy || 0) - overallAvg);
      const currentDiff = Math.abs((current.accuracy || 0) - overallAvg);
      return prevDiff < currentDiff ? prev : current;
    });
  };

  // Bar Chart Data - Subject wise accuracy
const generateBarChartData = () => {
  if (!apiData?.subjectWiseStats) return [];

  return apiData.subjectWiseStats.map(sub => ({
    name: sub.subjectName,
    accuracy: sub.accuracy || 0,
    correct: sub.correct || 0,
    wrong: sub.wrong || 0,
    left: sub.unanswered || 0,
  }));
};


  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "auto" }}>

        <h3>Loading Paper Analysis...</h3>
      </div>
    );
  }

  // Error state
  if (error || !apiData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>

        <h3>Error Loading Analysis...</h3>
      </div>
    );
  }

  const barChartData = generateBarChartData();
  const overallStats = apiData.overallStats || {};

  const completedStudents = overallStats.completedStudents || 0;
  const totalStudents = overallStats.totalStudents || 0;
  const notCompletedStudents = Math.max(totalStudents - completedStudents, 0);

  const subjectStats = apiData.subjectWiseStats || [];

  // console.log("Subject Wise Distribution:", subjectWiseDistribution);

  return (
    <div className="dashboard-container">
      {/* Header */}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>

        <div
          onClick={() => navigate(`/attempted-list/${id}`)}
          style={{
            marginLeft: "auto",
            marginRight: "16px",
            background: "#fee2e2",
            color: "#991b1b",
            padding: "8px 14px",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
          ❌ Not Completed: {notCompletedStudents} Students
        </div>
      </div>

      <div className="dashboard-header">
        <div className="header-left">
          <h1>📊 Paper Analysis</h1>
          <p>Performance insights and student distribution analysis</p>
        </div>
        <div className="header-right">
          <div className="paper-info">
            <span className="paper-badge">Paper #{id}</span>

          </div>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2>📈 Overall Performance</h2>
          <div className="paper-id-badge">
            Paper ID: {apiData.paperId || id}
          </div>
        </div>
        <div className="card-content">
          <div className="stats-grid">
            {[
              { label: 'Total Students', value: overallStats.totalStudents || 0, color: '#3b82f6' },
              {
                label: 'Correct %',
                value: `${Math.round(Number(overallStats.correctPercentage || 0))}%`,
                color: '#10b981'
              },
              {
                label: 'Wrong %',
                value: `${Math.round(Number(overallStats.wrongPercentage || 0))}%`,
                color: '#ef4444'
              },
              {
                label: 'Left %',
                value: `${Math.round(Number(overallStats.unansweredPercentage || 0))}%`,
                color: '#f59e0b'
              }

            ].map((stat, idx) => (
              <div key={idx} className="stat-card">
                <div className="stat-icon" style={{ background: `${stat.color}20`, color: stat.color }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{stat.value}</div>
                </div>
                <div className="stat-content">
                  <h3>{stat.label}</h3>
                  <div className="stat-value">{stat.value}</div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{
                      width: `${parseInt(stat.value) || 0}%`,
                      backgroundColor: stat.color
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Row 1: Marks Distribution and Subject Chart */}
        <div className="grid-row-1">
          {/* Marks Distribution */}
          <div className="dashboard-card marks-card">
            <div className="card-header">
              <h2>🎯 Marks Distribution</h2>
            </div>
            <div className="card-content">
              {marksLoading ? (
                <div className="loading-small">Loading distribution...</div>
              ) : marksDistributionData ? (
                <div className="marks-distribution-content">
                  <table className="marks-table">
                    <thead>
                      <tr>
                        <th>Marks Range</th>
                        <th>Students</th>
                        <th>Distribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marksDistributionData.marksDistribution && marksDistributionData.marksDistribution.length > 0 ? (
                        marksDistributionData.marksDistribution.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.range}</td>
                            <td
                              className="clickable-cell"
                              onClick={() => openStudentModal(item.students, item.range, item.percentage)}
                            >
                              {item.studentCount}
                            </td>
                            <td>
                              {item.percentage}%
                              <div className="distribution-bar">
                                <div
                                  className="distribution-fill"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="empty-state">
                            No marks distribution data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {marksDistributionData.statistics && (
                    <div className="marks-stats-summary">
                      <div className="stat-item">
                        <span>Total Marks</span>
                        <strong>{marksDistributionData.totalMarks || marksDistributionData.statistics.totalMarks}</strong>
                      </div>
                      <div className="stat-item">
                        <span>Average Marks</span>
                        <strong>{marksDistributionData.statistics.averageMarks}</strong>
                      </div>
                      <div className="stat-item">
                        <span>Highest Marks</span>
                        <strong>{marksDistributionData.statistics.highestMarks}</strong>
                      </div>
                      <div className="stat-item">
                        <span>Lowest Marks</span>
                        <strong>{marksDistributionData.statistics.lowestMarks}</strong>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">No marks distribution data available</div>
              )}
            </div>
          </div>

          {/* Subject-wise Accuracy Chart */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>📊 Subject-wise Accuracy</h2>
            </div>
            <div className="card-content">
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer>
                  <BarChart data={barChartData} barSize={36}>
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.3} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#6b7280" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 13, fill: "#6b7280" }} />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        fontSize: "13px",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="accuracy" fill="url(#barGradient)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Subject-wise Analysis Table */}
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2>🎓 Subject Analysis</h2>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
              Click on a subject to view chapters, then click on a chapter to view topics
            </p>
          </div>
          <div className="card-content">
            {subjectStats.length > 0 ? (
              <div className="performance-table-container">
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Subject Name</th>
                      <th>Students</th>
                      <th>Correct %</th>
                      <th>Wrong %</th>
                      <th>Left %</th>
                      <th>Performance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectStats.map((subject, idx) => (
                      <React.Fragment key={subject.subjectId}>
                        {/* Subject Row */}
                        <tr style={{
                          backgroundColor: expandedSubject === subject.subjectId ? '#f0f9ff' : '#fff',
                          cursor: 'pointer'
                        }}
                          onClick={() => toggleSubject(subject.subjectId)}
                        >
                          <td>
                            <div className="student-rank">{idx + 1}</div>
                          </td>
                          <td>
                            <div className="student-name">{subject.subjectName}</div>
                          </td>
                          <td>{overallStats.totalStudents || 0}</td>
                          <td>
                            <div className="accuracy-badge">
                              {subject.correctPercentage || 0}%
                            </div>
                          </td>
                          <td>
                            <div className="accuracy-badge poor">
                              {subject.wrongPercentage || 0}%
                            </div>
                          </td>
                          <td>
                            <div className="accuracy-badge" style={{ background: '#e5e7eb', color: '#6b7280' }}>
                              {subject.unansweredPercentage || 0}%
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const performance = getPerformanceLevel(subject.accuracy || 0);
                              return (
                                <div className="performance-indicator" style={{
                                  backgroundColor: performance.color + '20',
                                  color: performance.color
                                }}>
                                  {performance.text}
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <button className="view-details-btn">
                              {expandedSubject === subject.subjectId ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>

                        {/* Chapters for this subject */}
                        {expandedSubject === subject.subjectId && getChaptersForSubject(subject.subjectId).map((chapter, chapIdx) => (
                          <React.Fragment key={chapter.chapterId}>
                            {/* Chapter Row */}
                            <tr style={{
                              backgroundColor: '#f8fafc',
                              cursor: 'pointer'
                            }}
                              onClick={() => toggleChapter(chapter.chapterId)}
                            >
                              <td style={{ paddingLeft: '32px' }}>
                                {chapIdx + 1}
                              </td>
                              <td style={{ paddingLeft: '32px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Chapter:</span>
                                  <span style={{ fontSize: '0.85rem' }}>{chapter.chapterName}</span>
                                </div>
                              </td>
                              <td>{overallStats.totalStudents || 0}</td>
                              <td>
                                <div className="accuracy-badge">
                                  {chapter.correctPercentage || 0}%
                                </div>
                              </td>
                              <td>
                                <div className="accuracy-badge poor">
                                  {chapter.wrongPercentage || 0}%
                                </div>
                              </td>
                              <td>
                                <div className="accuracy-badge" style={{ background: '#e5e7eb', color: '#6b7280' }}>
                                  {chapter.unansweredPercentage || 0}%
                                </div>
                              </td>
                              <td>
                                {(() => {
                                  const performance = getPerformanceLevel(chapter.accuracy || 0);
                                  return (
                                    <div className="performance-indicator" style={{
                                      backgroundColor: performance.color + '20',
                                      color: performance.color
                                    }}>
                                      {performance.text}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td>
                                <button className="view-details-btn" style={{ background: '#10b981' }}>
                                  {expandedChapter === chapter.chapterId ? 'Hide Topics' : 'View Topics'}
                                </button>
                              </td>
                            </tr>

                            {/* Topics for this chapter */}
                            {expandedChapter === chapter.chapterId && getTopicsForChapter(chapter.chapterId).map((topic, topicIdx) => (
                              <tr key={topic.topicId} style={{ backgroundColor: '#f1f5f9' }}>
                                <td style={{ paddingLeft: '48px' }}>
                                  {topicIdx + 1}
                                </td>
                                <td style={{ paddingLeft: '48px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>Topic:</span>
                                    <span style={{ fontSize: '0.85rem' }}>{topic.topicName}</span>
                                  </div>
                                </td>
                                <td>{overallStats.totalStudents || 0}</td>
                                <td>
                                  <div className="accuracy-badge">
                                    {topic.correctPercentage || 0}%
                                  </div>
                                </td>
                                <td>
                                  <div className="accuracy-badge poor">
                                    {topic.wrongPercentage || 0}%
                                  </div>
                                </td>
                                <td>
                                  <div className="accuracy-badge" style={{ background: '#e5e7eb', color: '#6b7280' }}>
                                    {topic.unansweredPercentage || 0}%
                                  </div>
                                </td>
                                <td>
                                  {(() => {
                                    const performance = getPerformanceLevel(topic.accuracy || 0);
                                    return (
                                      <div className="performance-indicator" style={{
                                        backgroundColor: performance.color + '20',
                                        color: performance.color
                                      }}>
                                        {performance.text}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td>
                                  <div style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280',
                                    backgroundColor: '#f3f4f6',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontWeight: 500
                                  }}>

                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                No subject data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Student Details Modal */}
      {showStudentModal && (
        <div className="modal-overlay" onClick={closeStudentModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                👨‍🎓 Students: {selectedRangeInfo.range} ({selectedRangeInfo.percentage}%)
                <span style={{
                  fontSize: '0.85rem',
                  opacity: 0.9,
                  marginLeft: '10px'
                }}>
                  {selectedRangeStudents.length} student{selectedRangeStudents.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <button className="close-btn" onClick={closeStudentModal}>×</button>
            </div>
            <div className="modal-content">
              {selectedRangeStudents.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.85rem",
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6" }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Student Name</th>
                        <th style={thStyle}>Student ID</th>
                        <th style={thStyle}>Correct</th>
                        <th style={thStyle}>Wrong</th>
                        <th style={thStyle}>Obtained Marks</th>
                        <th style={thStyle}>Attempted At</th>
                        <th style={thStyle}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRangeStudents.map((stu, idx) => (
                        <tr key={idx} style={rowStyle}>
                          <td style={tdCenter}>{idx + 1}</td>
                          <td style={tdStyle}>{stu.studentName}</td>
                          <td style={tdCenter}>{stu.studentId}</td>
                          <td style={{ ...tdCenter, color: "#10b981", fontWeight: 600 }}>
                            {stu.correctAnswers}
                          </td>
                          <td style={{ ...tdCenter, color: "#ef4444", fontWeight: 600 }}>
                            {stu.wrongAnswers}
                          </td>
                          <td style={{ ...tdCenter, color: "#3b82f6", fontWeight: 600 }}>
                            {stu.obtainedMarks}
                          </td>
                          <td style={tdCenter}>
                            {new Date(stu.attemptedAt).toLocaleString()}
                          </td>
                          <td style={tdCenter}>
                            <button
                              style={{
                                background: "#3b82f6",
                                color: "white",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "none",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                document.body.style.overflow = 'unset';
                                navigate(`/student-overall/${stu.studentId}`);
                              }}
                            >
                              View ➜
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">No students found in this range</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles - Updated to match AdminDashboard */}
      <style jsx>{`
        .dashboard-container {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          background: white;
          padding: 20px 24px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-left h1 {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .header-left p {
          color: #6b7280;
          margin: 4px 0 0;
          font-size: 14px;
        }

        .header-right {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .paper-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .paper-badge {
          background: #3b82f6;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .paper-name {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .paper-id-badge {
          font-size: 0.85rem;
          color: #6b7280;
          background-color: #f3f4f6;
          padding: 3px 8px;
          border-radius: 4px;
          font-weight: 500;
        }

        /* Dashboard Grid */
        .dashboard-grid {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .grid-row-1 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .dashboard-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 24px;
        }

        .dashboard-card.full-width {
          grid-column: 1 / -1;
        }

        .card-header {
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-header h2 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .card-content {
          padding: 20px;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .stat-card {
          background: white;
          padding: 16px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-content {
          flex: 1;
        }

        .stat-content h3 {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 4px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 4px;
        }

        .progress-bar {
          height: 6px;
          background: #f3f4f6;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 8px;
        }

        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 1s ease-in-out;
        }

        /* Performance Table */
        .performance-table-container {
          overflow-x: auto;
        }

        .performance-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        .performance-table thead {
          background-color: #f9fafb;
        }

        .performance-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .performance-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }

        .performance-table tbody tr:hover {
          background-color: #f9fafb;
        }

        .student-rank {
          width: 28px;
          height: 28px;
          background: #3b82f6;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 12px;
        }

        .student-name {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .accuracy-badge {
          display: inline-block;
          padding: 4px 10px;
          background: #d1fae5;
          color: #065f46;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
          min-width: 60px;
          text-align: center;
        }

        .accuracy-badge.poor {
          background: #fee2e2;
          color: #991b1b;
        }

        .performance-indicator {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
          min-width: 60px;
          text-align: center;
        }

        .view-details-btn {
          padding: 6px 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 60px;
        }

        .view-details-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        /* Marks Distribution */
        .marks-distribution-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .marks-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        .marks-table th {
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }

        .marks-table td {
          padding: 12px;
          border-bottom: 1px solid #f3f4f6;
        }

        .clickable-cell {
          color: #3b82f6;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clickable-cell:hover {
          background: #eff6ff;
        }

        .distribution-bar {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
          margin-top: 4px;
        }

        .distribution-fill {
          height: 100%;
          background: #3b82f6;
          border-radius: 4px;
          transition: width 1s ease-in-out;
        }

        .marks-stats-summary {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-item span {
          font-size: 0.85rem;
          color: #6b7280;
        }

        .stat-item strong {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1f2937;
        }

        /* Loading and Error States */
        .dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f8fafc;
        }

        .dashboard-spinner {
          width: 60px;
          height: 60px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        .loading-text {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
          text-align: center;
          animation: pulse 1.5s infinite;
        }

        .dashboard-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f8fafc;
          text-align: center;
        }

        .dashboard-error h3 {
          color: #dc2626;
          margin: 16px 0 8px;
        }

        .dashboard-error p {
          color: #6b7280;
          margin-bottom: 24px;
        }

        .dashboard-retry {
          padding: 10px 24px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }

        .loading-small, .empty-state {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
          backdrop-filter: blur(3px);
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 1200px;
          max-height: 80vh;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          animation: modalSlideUp 0.4s ease;
        }

        .modal-header {
          background: #3b82f6;
          padding: 20px;
          color: white;
          position: relative;
        }

        .modal-header h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          font-size: 1.2rem;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .modal-content {
          padding: 20px;
          max-height: 60vh;
          overflow-y: auto;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes modalSlideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .grid-row-1 {
            grid-template-columns: 1fr;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 992px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .marks-stats-summary {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .header-right {
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
          }
          
          .paper-info {
            width: 100%;
          }
          
          .stat-card {
            flex-direction: column;
            text-align: center;
            padding: 20px;
          }
          
          .stat-icon {
            width: 48px;
            height: 48px;
          }
        }

        @media (max-width: 576px) {
          .dashboard-container {
            padding: 16px;
          }
          
          .card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .performance-table {
            font-size: 0.75rem;
          }
          
          .performance-table th,
          .performance-table td {
            padding: 8px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default PaperAnalysis;